import type { ProgramScopedContentRow } from '../types/domain';
import { matchScheduleWithContent, type OrderedContentLike } from './scheduleContentMatcher';

/** `도덕|-` / `도덕|도덕A` → `도덕` */
export function trackNameFromProgramScopeKey(scopeKey: string): string {
  const s = (scopeKey ?? '').trim();
  if (!s) return '';
  const i = s.indexOf('|');
  return (i >= 0 ? s.slice(0, i) : s).trim();
}

/**
 * 편제 프로그램 행을 현재 연간 일정 칸에 맞춘다.
 * 스코프 행은 `order_index`(원래 순서)를 유지하고, i번째 내용 ↔ i번째 일정을 짝짓는다.
 * 일정 칸보다 많은 내용만 leftover(넘침)로 둔다. 칸 키 변경만으로 내용을 아래로 보내지 않는다.
 * 고정 프로그램은 이 경로를 쓰지 않는다.
 */
export function rematchTrackScopedRowsToScheduleSlots<T extends OrderedContentLike>(
  scopedRowsSorted: readonly T[],
  scheduleAnnualKeys: readonly string[]
): { slotRows: (T | undefined)[]; leftoverRows: T[] } {
  const { pairs, overflow } = matchScheduleWithContent(scheduleAnnualKeys, scopedRowsSorted);
  return { slotRows: pairs.map((p) => p.content ?? undefined), leftoverRows: overflow };
}

export function emptyTrackScopedSlotPlaceholder(params: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  scopeKey: string;
  orderIndex: number;
  sourceAnnualCellKey: string;
}): ProgramScopedContentRow {
  return {
    id: `virtual-track-slot-${params.orderIndex}`,
    workspace_id: params.workspaceId,
    grade_id: params.gradeId,
    academic_year: params.academicYear,
    term_number: params.termNumber,
    scope_type: 'track',
    scope_key: params.scopeKey,
    order_index: params.orderIndex,
    source_annual_cell_key: params.sourceAnnualCellKey,
    timetable_text: '',
    unit_text: '',
    learning_text: '',
    learning_note: '',
    presentation_ref_id: null,
    worksheet_ref_id: null,
    page_text: '',
    assistant_page_text: '',
    current_period_text: '',
    total_period_text: '',
    material_text: '',
    record_cross_curricular_text: '',
    record_memo_text: '',
    record_evaluation_text: '',
    record_class_text: '',
    bg_color_by_field: {},
  };
}

/**
 * 일정 슬롯 순서에 스코프 행을 그대로 붙인다.
 * 슬롯이 더 많으면 placeholder, 내용이 더 많으면 맨 뒤 넘침 행(일정 열 비움).
 */
export function assembleTrackScopedRowsForSchedule(
  scopedRows: readonly ProgramScopedContentRow[],
  scheduleAnnualKeys: readonly string[],
  placeholderMeta: {
    workspaceId: string;
    gradeId: string;
    academicYear: number;
    termNumber: 1 | 2;
    scopeKey: string;
  }
): ProgramScopedContentRow[] {
  const { slotRows, leftoverRows } = rematchTrackScopedRowsToScheduleSlots(scopedRows, scheduleAnnualKeys);
  const slots = slotRows.map((row, i) => {
    const key = scheduleAnnualKeys[i] ?? '';
    if (row) {
      return {
        ...row,
        order_index: i,
        source_annual_cell_key: key,
      };
    }
    return emptyTrackScopedSlotPlaceholder({
      ...placeholderMeta,
      orderIndex: i,
      sourceAnnualCellKey: key,
    });
  });
  const leftovers = leftoverRows.map((row, j) => ({
    ...row,
    order_index: slots.length + j,
    source_annual_cell_key: null,
  }));
  return [...slots, ...leftovers];
}
